import { createContext, useState, useEffect, useContext } from 'react'
import { LoginRequest } from '../types/LoginRequest.model'
import { ReactNode } from 'react'
import { LoginResult } from '../types/LoginResult.model'
import { APP_CONST } from '../constants/App.const'
import { authService } from '../services/auth.service'
import { User } from '../types/user.model'

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  login: (user: LoginRequest, eventName: string, isSilenceLogin: boolean) => Promise<LoginResult>
  logout: () => void,
  getLoginToken: () => string | null,
  isSilenceLogin: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}


export const AuthProvider = ({ children }: { children: ReactNode }) => {

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  const [isSilenceLogin, setIsSilenceLogin] = useState(false);

  useEffect(() => {
    // Check if user is already logged in from localStorage
    const savedUser = localStorage.getItem(APP_CONST.USER);
    const savedToken = localStorage.getItem(APP_CONST.USER_TOKEN);

    const params = new URLSearchParams(window.location.search);

    const parmEventName = params.get("eventName");
    const parmEmail = params.get("email");
    const parmRegId = params.get("regId");

    if (savedUser && savedToken) {
      const savedUserJson = JSON.parse(savedUser);

      if ((!parmEventName || parmEventName === savedUserJson.eventName) &&
        (!parmEmail || parmEmail === savedUserJson.email) &&
        (!parmRegId || parmRegId === savedUserJson.regId)) {
        setUser(savedUserJson);
        setIsAuthenticated(true);
      }
    }

    setIsSilenceLogin(parmEventName && parmEmail && parmRegId ? true : false);
  }, [])

  const login = async (loginRequest: LoginRequest, eventName: string, isSilenceLogin: boolean = false): Promise<LoginResult> => {
    const result = await authService.login(loginRequest, eventName);
    if (result.success && result.user) {

      const userToSave: User = {
        name: result.user.name,
        email: loginRequest.email,
        eventName: eventName,
      }
      setUser(userToSave);
      setIsAuthenticated(true);
      setIsSilenceLogin(!!isSilenceLogin);
      localStorage.setItem(APP_CONST.LOGIN_TOKEN, loginRequest.userToken);
      localStorage.setItem(APP_CONST.USER, JSON.stringify(userToSave));
      localStorage.setItem(APP_CONST.USER_TOKEN, JSON.stringify(result.token));
    };
    return result;
  }


  const logout = () => {
    setUser(null)
    setIsAuthenticated(false)
    localStorage.removeItem(APP_CONST.USER);
    localStorage.removeItem(APP_CONST.USER_TOKEN);
  }

  const getLoginToken = () => localStorage.getItem(APP_CONST.LOGIN_TOKEN);


  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    getLoginToken,
    isSilenceLogin
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 